import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Flame, Home, Droplets, Users, Phone, ArrowRight, ShieldCheck, HeartHandshake } from "lucide-react";
import { FadeIn } from "../components/motion";
import { useT } from "../lib/i18n";
import { LanguageToggle } from "../components/LanguageToggle";

const TYPES = [
  { icon: Flame, t: "Fire" }, { icon: Droplets, t: "Flood" }, { icon: Home, t: "Sudden homelessness" },
  { icon: Users, t: "Displacement" }, { icon: AlertTriangle, t: "Food emergency" }, { icon: ShieldCheck, t: "Building damage" },
];

const PRIORITY = [
  { code: "P1", label: "Critical", desc: "Immediate risk to life, safety or shelter.", color: "bg-red-500" },
  { code: "P2", label: "High", desc: "Serious hardship requiring rapid intervention.", color: "bg-orange-500" },
  { code: "P3", label: "Moderate", desc: "Significant need but no immediate threat.", color: "bg-yellow-500" },
  { code: "P4", label: "Standard", desc: "Important request that follows normal assessment.", color: "bg-emerald-500" },
];

export default function Emergency() {
  const t = useT();
  useEffect(() => { document.title = "Emergency & Crisis Support | Dutsen Kauna Foundation"; }, []);
  return (
    <div data-testid="emergency-page">
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <img src="https://images.pexels.com/photos/6995301/pexels-photo-6995301.jpeg" alt="Emergency relief supplies being distributed to displaced families" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <div className="flex flex-wrap items-center justify-between gap-3"><span className="text-[#d6aa43] tracking-[0.35em] text-xs font-semibold uppercase">{t("Emergency & crisis support")}</span><LanguageToggle /></div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-4xl leading-[1.05]">
            {t("When crisis happens,")} <span className="italic text-[#d6aa43]">{t("help must move quickly.")}</span>
          </h1>
          <p className="mt-6 text-white text-lg max-w-3xl">
            {t("Emergency assistance for individuals, families and communities facing sudden and serious hardship — where delay could significantly worsen safety, shelter, food security or wellbeing.")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/emergency/help" className="dk-btn-primary" data-testid="emerg-apply">
              {t("Request emergency help")} <ArrowRight size={16} />
            </Link>
            <Link to="/apply?category=emergency" className="dk-btn-outline" data-testid="emerg-report">
              {t("Report a community emergency")}
            </Link>
            <Link to="/donate?programme=emergency" className="text-sm text-[#d6aa43] hover:underline self-center" data-testid="emerg-donate">
              {t("Donate to emergency relief")} →
            </Link>
          </div>
          <div className="mt-8 p-4 rounded-lg bg-red-900/40 border border-red-500/40 flex items-start gap-3 max-w-3xl">
            <AlertTriangle className="text-red-300 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-red-100">
              <strong>{t("Safety notice:")}</strong> {t("Where there is immediate danger to life, violence, serious crime or fire requiring statutory services, contact appropriate emergency authorities first. DKF is a charitable support organisation and does not replace emergency services.")}
            </div>
          </div>
        </div>
      </section>

      {/* Emergency types */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <FadeIn>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">{t("What counts as an emergency")}</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mt-3 mb-8">{t("Situations we can respond to.")}</h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TYPES.map((ty) => {
              const Icon = ty.icon;
              return (
                <div key={ty.t} className="p-5 rounded-xl bg-[#f8f4e8] border border-black/5 text-center">
                  <Icon size={20} className="text-[#d6aa43] mx-auto mb-2" />
                  <div className="text-sm font-semibold text-[#0a0d12]">{t(ty.t)}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 p-4 rounded-md bg-[#f8f4e8] text-sm text-[#0a0d12]/80">
            {t("Submitting an emergency application does not automatically guarantee support. The Foundation must verify the situation and assess what assistance it can responsibly provide.")} <strong>{t("Applications are free.")}</strong>
          </div>
        </div>
      </section>

      {/* Priority classification */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">{t("Priority classification")}</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 mb-8">{t("Every case is triaged.")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {PRIORITY.map((p) => (
              <div key={p.code} className="p-5 rounded-xl border border-white/10 bg-white/[0.03]">
                <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${p.color}`}>{p.code}</div>
                <div className="font-serif text-xl font-bold mt-3">{t(p.label)}</div>
                <p className="text-sm text-white mt-2">{t(p.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rapid review */}
      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container max-w-4xl">
          <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">{t("Rapid review process")}</span>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mt-3 mb-8">{t("From application to support delivered.")}</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {["Application", "Safety review", "Verification", "Priority classification", "Decision", "Direct support / referral", "Delivery", "Follow-up"].map((s, i) => (
              <div key={s} className="p-4 rounded-md bg-white border border-black/5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#d6aa43] font-bold">{t("Step")} 0{i + 1}</div>
                <div className="font-serif text-lg text-[#0a0d12] font-bold mt-1">{t(s)}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 rounded-xl bg-[#155e3f] text-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <HeartHandshake className="text-[#d6aa43] flex-shrink-0" size={28} />
            <div className="flex-1">
              <div className="font-serif text-xl font-bold">{t("Every emergency case gets a serial")}</div>
              <p className="text-sm text-white">{t("Format:")} <span className="font-mono text-[#d6aa43]">DKF-HOU-EMG-2026-XXXXXX</span> — {t("trackable from application through follow-up.")}</p>
            </div>
            <Link to="/emergency/help" className="dk-btn-primary" data-testid="emerg-start-btn">
              {t("Start emergency request")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
